import Papa from 'papaparse';
import * as XLSX from 'xlsx';


// 0. Cell Parser
// 1) AHP origin, BWM (non)linear
export function parseNumber(x) {
  if (x == null) return NaN;

  const s = String(x).trim();
  if (!s) return NaN;
  if (s.includes('/')) {
    const [a, b] = s.split('/').map(Number);
    return isFinite(a) && isFinite(b) && b !== 0 ? a / b : NaN;
  }
  const n = Number(s);
  return isFinite(n) ? n : NaN;
}
// 2) fuzzy
export function parseFuzzyCell(x) {
  if (x == null) return [NaN, NaN, NaN];

  const s = String(x).trim();
  if (s === '0') return 0;

  if (!s) return [NaN, NaN, NaN];
  const inner = s.replace(/^[\(\[]\s*/, '').replace(/\s*[\)\]]$/, '');  // remove parentheses: both are allowed (1,2,3), [1,2,3] 

  if (inner.includes(',')) {
    const parts = inner.split(',').map(p => parseNumber(p));
    if (parts.length !== 3 || parts.some(v => !isFinite(v))) {
      return [NaN, NaN, NaN];
    }
    const [l, m, u] = parts;
    return [l, m, u];
  }

  // if input only one scalar v, then (v,v,v)
  // const v = parseNumber(inner);
  // if (!isFinite(v)) return [NaN, NaN, NaN];
  // return [v, v, v];
}
// 3) linguistic fuzzy
const AHP_LINGUISTIC_CODES = new Set([
  'EI',  
  'WLI', 'WMI',
  'FLI', 'FMI',
  'VLI', 'VMI',
  'ALI', 'AMI',
]);

export function parseLinguisticAHPFuzzyCell(x) {
  if (x == null) return null;

  const s = String(x).trim().toUpperCase();
  if (!s) return null;
  if (s === '0') return 0;
  if (!AHP_LINGUISTIC_CODES.has(s)) return null;

  return s; // string
}

const BWM_LINGUISTIC_CODES = new Set([
  'EI',  
  'WI', 
  'FI', 
  'VI', 
  'AI', 
]);
export function parseLinguisticBWMFuzzyCell(x) {
  if (x == null) return null;

  const s = String(x).trim().toUpperCase();
  if (!s) return null;
  if (s === '0') return 0;
  if (!BWM_LINGUISTIC_CODES.has(s)) return null;

  return s; // string
}


// 1. CSV Parser
// CSV → { criteria: string[], matrix: number[][] }

// 1) AHP origin, BWM (non)linear
export function importMatrixCsv(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: ({ data }) => {
        try {
          const rows = data;
          if (!rows.length) throw new Error('Empty file.');

          // Row 1: header (first cell = label/blank, then criteria names)
          const header = rows[0].map(x => String(x ?? '').trim());
          const criteria = header.slice(1).filter(Boolean);
          const n = criteria.length;
          if (n < 2) throw new Error('Header must list at least 2 criteria.');

          // Body should contain n rows
          const body = rows.slice(1);
          if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

          // Read each row label + n values
          const matrix = Array.from({ length: n }, (_, i) => {
            const r = body[i] ?? [];
            return Array.from({ length: n }, (_, j) => parseNumber(r[j + 1]));
          });

          resolve({ criteria, matrix });
        } catch (e) { reject(e); }
      },
      error: reject,
    });
  });
}

// 2) Fuzzy 
export function importFuzzyMatrixCsv(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: ({ data }) => {
        try {
          const rows = data;
          if (!rows.length) throw new Error('Empty file.');

          // Row 1: header (first cell = label/blank, then criteria names)
          const header = rows[0].map((x) => String(x ?? '').trim());
          const criteria = header.slice(1).filter(Boolean);
          const n = criteria.length;
          if (n < 2) throw new Error('Header must list at least 2 criteria.');

          // Body should contain n rows
          const body = rows.slice(1);
          if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

          // Read each row label + n fuzzy values
          const matrix = Array.from({ length: n }, (_, i) => {
            const r = body[i] ?? [];
            return Array.from({ length: n }, (_, j) => parseFuzzyCell(r[j + 1]));
          });

          resolve({ criteria, matrix });
        } catch (e) {
          reject(e);
        }
      },
      error: reject,
    });
  });
}

// 3) linguistic fuzzy
export function importAHPLinguisticMatrixCsv(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: ({ data }) => {
        try {
          const rows = data;
          if (!rows.length) throw new Error('Empty file.');

          // Row 1: header (first cell = label/blank, then criteria names)
          const header = rows[0].map(x => String(x ?? '').trim());
          const criteria = header.slice(1).filter(Boolean);
          const n = criteria.length;
          if (n < 2) throw new Error('Header must list at least 2 criteria.');

          // Body should contain n rows
          const body = rows.slice(1);
          if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

          // Read each row label + n values
          const matrix = Array.from({ length: n }, (_, i) => {
            const r = body[i] ?? [];
            return Array.from({ length: n }, (_, j) => parseLinguisticAHPFuzzyCell(r[j+1]));
          });

          resolve({ criteria, matrix });
        } catch (e) { reject(e); }
      },
      error: reject,
    });
  });
}

export function importBWMLinguisticMatrixCsv(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: ({ data }) => {
        try {
          const rows = data;
          if (!rows.length) throw new Error('Empty file.');

          // Row 1: header (first cell = label/blank, then criteria names)
          const header = rows[0].map(x => String(x ?? '').trim());
          const criteria = header.slice(1).filter(Boolean);
          const n = criteria.length;
          if (n < 2) throw new Error('Header must list at least 2 criteria.');

          // Body should contain n rows
          const body = rows.slice(1);
          if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

          // Read each row label + n values
          const matrix = Array.from({ length: n }, (_, i) => {
            const r = body[i] ?? [];
            return Array.from({ length: n }, (_, j) => parseLinguisticBWMFuzzyCell(r[j+1]));
          });

          resolve({ criteria, matrix });
        } catch (e) { reject(e); }
      },
      error: reject,
    });
  });
}

// 2. XLSX Parser
// XLSX (first sheet) → {criteria, matrix} (assumes same layout as CSV)

// 1) AHP origin, BWM (non)linear
export async function importMatrixXlsx(file) {
  const arrayBuffer = await file.arrayBuffer();
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('No sheet found.');
  const ws = wb.Sheets[sheetName];

  // header:1 → 2D array ([[...], [...], ...])
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
  if (!rows.length) throw new Error('Empty sheet.');

  const header = rows[0].map(x => String(x ?? '').trim());
  const criteria = header.slice(1).filter(Boolean);
  const n = criteria.length;
  if (n < 2) throw new Error('Header must list at least 2 criteria.');

  const body = rows.slice(1);
  if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

  const matrix = Array.from({ length: n }, (_, i) => {
    const r = body[i] ?? [];
    return Array.from({ length: n }, (_, j) => parseNumber(r[j + 1]));
  });

  return { criteria, matrix };
}

// 2) fuzzy
export async function importFuzzyMatrixXlsx(file) {
  const arrayBuffer = await file.arrayBuffer();
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('No sheet found.');
  const ws = wb.Sheets[sheetName];

  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
  if (!rows.length) throw new Error('Empty sheet.');

  const header = rows[0].map(x => String(x ?? '').trim());
  const criteria = header.slice(1).filter(Boolean);
  const n = criteria.length;
  if (n < 2) throw new Error('Header must list at least 2 criteria.');

  const body = rows.slice(1);
  if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

  const matrix = Array.from({ length: n }, (_, i) => {
    const r = body[i] ?? [];
    return Array.from({ length: n }, (_, j) => parseFuzzyCell(r[j + 1]));
  });

  return { criteria, matrix };
}

// 3) linguistic fuzzy
export async function importAHPLinguisticMatrixXlsx(file) {
  const arrayBuffer = await file.arrayBuffer();
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('No sheet found.');
  const ws = wb.Sheets[sheetName];

  // header:1 → 2D array ([[...], [...], ...])
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
  if (!rows.length) throw new Error('Empty sheet.');

  const header = rows[0].map(x => String(x ?? '').trim());
  const criteria = header.slice(1).filter(Boolean);
  const n = criteria.length;
  if (n < 2) throw new Error('Header must list at least 2 criteria.');

  const body = rows.slice(1);
  if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

  const matrix = Array.from({ length: n }, (_, i) => {
    const r = body[i] ?? [];
    return Array.from({ length: n }, (_, j) => parseLinguisticAHPFuzzyCell(r[j + 1]));
  });

  return { criteria, matrix };
}

export async function importBWMLinguisticMatrixXlsx(file) {
  const arrayBuffer = await file.arrayBuffer();
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('No sheet found.');
  const ws = wb.Sheets[sheetName];

  // header:1 → 2D array ([[...], [...], ...])
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
  if (!rows.length) throw new Error('Empty sheet.');

  const header = rows[0].map(x => String(x ?? '').trim());
  const criteria = header.slice(1).filter(Boolean);
  const n = criteria.length;
  if (n < 2) throw new Error('Header must list at least 2 criteria.');

  const body = rows.slice(1);
  if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

  const matrix = Array.from({ length: n }, (_, i) => {
    const r = body[i] ?? [];
    return Array.from({ length: n }, (_, j) => parseLinguisticBWMFuzzyCell(r[j + 1]));
  });

  return { criteria, matrix };
}

// 3. file dispatch
// Auto-dispatch by file extension to CSV/XLSX parser 

// 1) AHP origin, BWM (non)linear
export async function importMatrixFile(file) {
  const name = (file?.name || '').toLowerCase();
  if (name.endsWith('.csv')) return importMatrixCsv(file);
  if (name.endsWith('.xlsx')) return importMatrixXlsx(file);

  // Fallback by MIME type (some browsers report only MIME)
  const type = file?.type || '';
  if (type.includes('csv')) return importMatrixCsv(file);
  if (type.includes('sheet') || type.includes('excel')) return importMatrixXlsx(file);
  
  throw new Error('Unsupported file type. Please upload .csv or .xlsx');
}
// 2) fuzzy
export async function importFuzzyMatrixFile(file) {
  const name = (file?.name || '').toLowerCase();
  if (name.endsWith('.csv')) return importFuzzyMatrixCsv(file);
  if (name.endsWith('.xlsx')) return importFuzzyMatrixXlsx(file);

  const type = file?.type || '';
  if (type.includes('csv')) return importFuzzyMatrixCsv(file);
  if (type.includes('sheet') || type.includes('excel')) return importFuzzyMatrixXlsx(file);

  throw new Error('Unsupported file type. Please upload .csv or .xlsx');
}
// 3) linguistic fuzzy
export async function importAHPLinguisticMatrixFile(file) {
  const name = (file?.name || '').toLowerCase();
  if (name.endsWith('.csv')) return importAHPLinguisticMatrixCsv(file); 
  if (name.endsWith('.xlsx')) return importAHPLinguisticMatrixXlsx(file);

  // Fallback by MIME type (some browsers report only MIME)
  const type = file?.type || '';
  if (type.includes('csv')) return importAHPLinguisticMatrixCsv(file);
  if (type.includes('sheet') || type.includes('excel')) return importAHPLinguisticMatrixXlsx(file);
  
  throw new Error('Unsupported file type. Please upload .csv or .xlsx');
}

export async function importBWMLinguisticMatrixFile(file) {
  const name = (file?.name || '').toLowerCase();
  if (name.endsWith('.csv')) return importBWMLinguisticMatrixCsv(file);
  if (name.endsWith('.xlsx')) return importBWMLinguisticMatrixXlsx(file);

  // Fallback by MIME type (some browsers report only MIME)
  const type = file?.type || '';
  if (type.includes('csv')) return importBWMLinguisticMatrixCsv(file);
  if (type.includes('sheet') || type.includes('excel')) return importBWMLinguisticMatrixXlsx(file);
  
  throw new Error('Unsupported file type. Please upload .csv or .xlsx');
}

// 4) Flexible AHP import (cross-variant)
const AHP_LINGUISTIC_TO_TFN = {
  EI: [1, 1, 1],
  WMI: [2 / 3, 1, 3 / 2],
  FMI: [3 / 2, 2, 5 / 2],
  VMI: [5 / 2, 3, 7 / 2],
  AMI: [7 / 2, 4, 9 / 2],
  WLI: [2 / 3, 1, 3 / 2],
  FLI: [2 / 5, 1 / 2, 2 / 3],
  VLI: [2 / 7, 1 / 3, 2 / 5],
  ALI: [2 / 9, 1 / 4, 2 / 7],
};

const AHP_MI_LABELS = ['WMI', 'FMI', 'VMI', 'AMI'];
const AHP_LI_LABELS = ['WLI', 'FLI', 'VLI', 'ALI'];
const AHP_ALL_LABELS = ['EI', ...AHP_MI_LABELS, ...AHP_LI_LABELS];
const AHP_LINGUISTIC_RECIPROCAL = {
  EI: 'EI',
  WMI: 'WLI',
  WLI: 'WMI',
  FMI: 'FLI',
  FLI: 'FMI',
  VMI: 'VLI',
  VLI: 'VMI',
  AMI: 'ALI',
  ALI: 'AMI',
};

const AHP_CANONICAL_SCALE = [
  1 / 9, 1 / 8, 1 / 7, 1 / 6, 1 / 5, 1 / 4, 1 / 3, 1 / 2,
  1,
  2, 3, 4, 5, 6, 7, 8, 9,
  2 / 3, 3 / 2, 5 / 2, 7 / 2,
  2 / 5, 2 / 7, 2 / 9,
];

// Import may carry rounded decimals (e.g. 0.333 for 1/3); snap those back to canonical values.
const AHP_SNAP_TOL = 1.5e-3;

function snapAhpNumber(x, tol = AHP_SNAP_TOL) {
  const value = Number(x);
  if (!Number.isFinite(value)) return value;

  let best = value;
  let bestDist = Infinity;
  for (const c of AHP_CANONICAL_SCALE) {
    const dist = Math.abs(value - c);
    if (dist < bestDist) {
      bestDist = dist;
      best = c;
    }
  }
  return bestDist <= tol ? best : value;
}

function snapAhpTfn(tfn) {
  if (!isFiniteTfn(tfn)) return tfn;
  return [snapAhpNumber(tfn[0]), snapAhpNumber(tfn[1]), snapAhpNumber(tfn[2])];
}

function isNearlyReciprocalAhpNumbers(a, b, tol = AHP_SNAP_TOL) {
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0 || b === 0) return false;
  return Math.abs(a * b - 1) <= tol;
}

function isNearlyReciprocalAhpTfn(a, b, tol = AHP_SNAP_TOL) {
  if (!isFiniteTfn(a) || !isFiniteTfn(b)) return false;
  return (
    Math.abs(a[0] * b[2] - 1) <= tol &&
    Math.abs(a[1] * b[1] - 1) <= tol &&
    Math.abs(a[2] * b[0] - 1) <= tol
  );
}

function parseAhpAnyCell(raw) {
  if (raw == null) return { kind: 'zero', value: 0 };
  if (isFiniteTfn(raw)) return { kind: 'fuzzy', value: raw.map(Number) };
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw === 0) return { kind: 'zero', value: 0 };
    return { kind: 'number', value: Number(raw) };
  }

  const s = String(raw).trim();
  if (!s || s === '0') return { kind: 'zero', value: 0 };

  const ling = parseLinguisticAHPFuzzyCell(s);
  if (typeof ling === 'string') return { kind: 'linguistic', value: ling };

  const fuzzy = parseFuzzyCell(s);
  if (isFiniteTfn(fuzzy)) return { kind: 'fuzzy', value: fuzzy.map(Number) };

  const num = parseNumber(s);
  if (Number.isFinite(num)) {
    if (num === 0) return { kind: 'zero', value: 0 };
    return { kind: 'number', value: num };
  }

  return { kind: 'invalid', value: s };
}

function ahpCenterFromCell(cell) {
  if (!cell || cell.kind === 'zero') return 0;
  if (cell.kind === 'number') return Number(cell.value);
  if (cell.kind === 'fuzzy') return Number(cell.value?.[1]);
  if (cell.kind === 'linguistic') return Number(AHP_LINGUISTIC_TO_TFN[cell.value]?.[1] ?? 1);
  return 0;
}

function nearestAhpLabelFromNumber(x) {
  const v = Number(x);
  if (!Number.isFinite(v)) return 'EI';
  if (Math.abs(v - 1) < 1e-9) return 'EI';

  const candidates = v > 1 ? AHP_MI_LABELS : AHP_LI_LABELS;
  let best = candidates[0];
  let bestDist = Infinity;
  for (const label of candidates) {
    const c = AHP_LINGUISTIC_TO_TFN[label][1];
    const d = Math.abs(v - c);
    if (d < bestDist) {
      bestDist = d;
      best = label;
    }
  }
  return best;
}

function nearestAhpLabelFromTfn(tfn) {
  if (!isFiniteTfn(tfn)) return 'EI';

  const center = Number(tfn[1]);
  let labelPool = AHP_ALL_LABELS;
  if (Math.abs(center - 1) < 1e-9) {
    labelPool = ['EI', 'WMI', 'WLI'];
  } else if (center > 1) {
    labelPool = ['EI', ...AHP_MI_LABELS];
  } else if (center < 1) {
    labelPool = ['EI', ...AHP_LI_LABELS];
  }

  let best = 'EI';
  let bestDist = Infinity;
  for (const label of labelPool) {
    const ref = AHP_LINGUISTIC_TO_TFN[label];
    const d =
      Math.pow(tfn[0] - ref[0], 2) +
      Math.pow(tfn[1] - ref[1], 2) +
      Math.pow(tfn[2] - ref[2], 2);
    if (d < bestDist) {
      bestDist = d;
      best = label;
    }
  }
  return best;
}

function canonicalAhpToTargetCell(cell, targetVariant, i, j) {
  const isDiagonal = i === j;
  const v = targetVariant;

  if (v === 'origin') {
    if (isDiagonal) return 1;
    if (!cell || cell.kind === 'zero') return 0;
    return ahpCenterFromCell(cell);
  }

  if (v === 'fuzzy') {
    if (isDiagonal) return [1, 1, 1];
    if (!cell || cell.kind === 'zero') return 0;
    if (cell.kind === 'fuzzy') return cell.value;
    if (cell.kind === 'linguistic') return AHP_LINGUISTIC_TO_TFN[cell.value] || [1, 1, 1];
    if (cell.kind === 'number') {
      const c = Number(cell.value);
      return [c, c, c];
    }
    return 0;
  }

  if (v === 'linguistic fuzzy') {
    if (isDiagonal) return 'EI';
    if (!cell || cell.kind === 'zero') return 0;
    if (cell.kind === 'linguistic') return cell.value;
    if (cell.kind === 'fuzzy') return nearestAhpLabelFromTfn(cell.value);
    if (cell.kind === 'number') return nearestAhpLabelFromNumber(cell.value);
    return 0;
  }

  // fallback to crisp/origin behavior
  if (isDiagonal) return 1;
  return ahpCenterFromCell(cell);
}

function reciprocalFuzzy(tfn) {
  if (!isFiniteTfn(tfn)) return [1, 1, 1];
  return [1 / tfn[2], 1 / tfn[1], 1 / tfn[0]];
}

function normalizeAHPMatrixForVariant(matrix, targetVariant) {
  const n = matrix.length;
  const out = matrix.map((row) => row.slice());

  if (targetVariant === 'origin') {
    for (let i = 0; i < n; i++) out[i][i] = 1;

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const upper = Number(out[i][j]);
        const lower = Number(out[j][i]);
        const upperValid = Number.isFinite(upper) && upper !== 0;
        const lowerValid = Number.isFinite(lower) && lower !== 0;

        if (upperValid && !lowerValid) {
          const normalizedUpper = snapAhpNumber(upper);
          out[i][j] = normalizedUpper;
          out[j][i] = snapAhpNumber(1 / normalizedUpper);
          continue;
        }

        if (lowerValid && !upperValid) {
          const normalizedLower = snapAhpNumber(lower);
          out[j][i] = normalizedLower;
          out[i][j] = snapAhpNumber(1 / normalizedLower);
          continue;
        }

        if (upperValid && lowerValid) {
          if (isNearlyReciprocalAhpNumbers(upper, lower)) {
            const normalizedUpper = snapAhpNumber(upper);
            out[i][j] = normalizedUpper;
            out[j][i] = snapAhpNumber(1 / normalizedUpper);
          } else {
            out[i][j] = snapAhpNumber(upper);
            out[j][i] = snapAhpNumber(lower);
          }
        }
      }
    }
    return out;
  }

  if (targetVariant === 'fuzzy') {
    for (let i = 0; i < n; i++) out[i][i] = [1, 1, 1];

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = out[i][j];
        const b = out[j][i];
        const aValid = isFiniteTfn(a);
        const bValid = isFiniteTfn(b);

        if (aValid && !bValid) {
          const normalizedA = snapAhpTfn(a);
          out[i][j] = normalizedA;
          out[j][i] = snapAhpTfn(reciprocalFuzzy(normalizedA));
          continue;
        }

        if (bValid && !aValid) {
          const normalizedB = snapAhpTfn(b);
          out[j][i] = normalizedB;
          out[i][j] = snapAhpTfn(reciprocalFuzzy(normalizedB));
          continue;
        }

        if (aValid && bValid) {
          const normalizedA = snapAhpTfn(a);
          const normalizedB = snapAhpTfn(b);

          if (isNearlyReciprocalAhpTfn(normalizedA, normalizedB)) {
            out[i][j] = normalizedA;
            out[j][i] = snapAhpTfn(reciprocalFuzzy(normalizedA));
          } else {
            out[i][j] = normalizedA;
            out[j][i] = normalizedB;
          }
        }
      }
    }
    return out;
  }

  if (targetVariant === 'linguistic fuzzy') {
    for (let i = 0; i < n; i++) out[i][i] = 'EI';

    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const a = out[i][j];
        const b = out[j][i];
        const aValid = typeof a === 'string' && AHP_LINGUISTIC_RECIPROCAL[a];
        const bValid = typeof b === 'string' && AHP_LINGUISTIC_RECIPROCAL[b];

        if (aValid && !bValid) out[j][i] = AHP_LINGUISTIC_RECIPROCAL[a];
        if (bValid && !aValid) out[i][j] = AHP_LINGUISTIC_RECIPROCAL[b];
      }
    }
    return out;
  }

  return out;
}

export async function importAHPMatrixFlexibleFile(file, targetVariant = 'origin') {
  const rows = await readRowsByFile(file);
  if (!rows.length) throw new Error('Empty file.');

  const header = (rows[0] || []).map((x) => String(x ?? '').trim());
  const criteria = header.slice(1).filter(Boolean);
  const n = criteria.length;
  if (n < 2) throw new Error('Header must list at least 2 criteria.');

  const body = rows.slice(1);
  if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

  const canonicalMatrix = Array.from({ length: n }, (_, i) => {
    const r = body[i] || [];
    return Array.from({ length: n }, (_, j) => parseAhpAnyCell(r[j + 1]));
  });

  const invalidCells = [];
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (canonicalMatrix[i][j]?.kind === 'invalid') {
        invalidCells.push(`(${i + 1},${j + 1})="${canonicalMatrix[i][j].value}"`);
      }
    }
  }
  if (invalidCells.length) {
    throw new Error(
      `Unsupported value(s) in matrix: ${invalidCells.slice(0, 5).join(', ')}${invalidCells.length > 5 ? ' ...' : ''}`
    );
  }

  const rawMatrix = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) =>
      canonicalAhpToTargetCell(canonicalMatrix[i][j], targetVariant, i, j)
    )
  );

  const matrix = normalizeAHPMatrixForVariant(rawMatrix, targetVariant);

  return { criteria, matrix };
}

// 4) Flexible BWM import (cross-variant)
const BWM_LINGUISTIC_TO_TFN = {
  EI: [1, 1, 1],
  WI: [2 / 3, 1, 3 / 2],
  FI: [3 / 2, 2, 5 / 2],
  VI: [5 / 2, 3, 7 / 2],
  AI: [7 / 2, 4, 9 / 2],
};

const BWM_LINGUISTIC_ORDER = ['EI', 'WI', 'FI', 'VI', 'AI'];

function isFiniteTfn(v) {
  return Array.isArray(v) && v.length === 3 && v.every(Number.isFinite);
}

function isZeroLikeCell(v) {
  if (v == null) return true;
  if (typeof v === 'number') return !Number.isFinite(v) || v === 0;
  if (typeof v === 'string') {
    const s = v.trim();
    return !s || s === '0';
  }
  if (isFiniteTfn(v)) {
    return v[0] === 0 && v[1] === 0 && v[2] === 0;
  }
  return false;
}

function tfnCenter(tfn) {
  return Number(tfn?.[1]);
}

function nearestLinguisticByCenter(center) {
  const c = Number(center);
  if (!Number.isFinite(c)) return 'EI';

  let best = 'EI';
  let bestDist = Infinity;
  for (const label of BWM_LINGUISTIC_ORDER) {
    const dist = Math.abs(c - BWM_LINGUISTIC_TO_TFN[label][1]);
    if (dist < bestDist) {
      bestDist = dist;
      best = label;
    }
  }
  return best;
}

function nearestLinguisticByTfn(tfn) {
  if (!isFiniteTfn(tfn)) return 'EI';
  let best = 'EI';
  let bestDist = Infinity;

  for (const label of BWM_LINGUISTIC_ORDER) {
    const ref = BWM_LINGUISTIC_TO_TFN[label];
    const d =
      Math.pow(tfn[0] - ref[0], 2) +
      Math.pow(tfn[1] - ref[1], 2) +
      Math.pow(tfn[2] - ref[2], 2);
    if (d < bestDist) {
      bestDist = d;
      best = label;
    }
  }
  return best;
}

function parseBwmAnyCell(raw) {
  if (raw == null) return { kind: 'zero', value: 0 };
  if (isFiniteTfn(raw)) return { kind: 'fuzzy', value: raw.map(Number) };
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw === 0) return { kind: 'zero', value: 0 };
    return { kind: 'number', value: Number(raw) };
  }

  const s = String(raw).trim();
  if (!s || s === '0') return { kind: 'zero', value: 0 };

  const ling = parseLinguisticBWMFuzzyCell(s);
  if (typeof ling === 'string') return { kind: 'linguistic', value: ling };

  const fuzzy = parseFuzzyCell(s);
  if (isFiniteTfn(fuzzy)) return { kind: 'fuzzy', value: fuzzy.map(Number) };

  const num = parseNumber(s);
  if (Number.isFinite(num)) {
    if (num === 0) return { kind: 'zero', value: 0 };
    return { kind: 'number', value: num };
  }

  return { kind: 'invalid', value: s };
}

function canonicalToCenterValue(cell) {
  if (!cell || cell.kind === 'zero') return 0;
  if (cell.kind === 'number') return cell.value;
  if (cell.kind === 'fuzzy') return tfnCenter(cell.value);
  if (cell.kind === 'linguistic') return BWM_LINGUISTIC_TO_TFN[cell.value]?.[1] ?? 1;
  return 0;
}

function canonicalToTargetCell(cell, targetVariant, i, j) {
  const isDiagonal = i === j;
  const v = targetVariant;

  if (v === 'linear' || v === 'nonlinear') {
    if (isDiagonal) return 1;
    if (!cell || cell.kind === 'zero') return 0;
    return canonicalToCenterValue(cell);
  }

  if (v === 'fuzzy') {
    if (isDiagonal) return [1, 1, 1];
    if (!cell || cell.kind === 'zero') return 0;
    if (cell.kind === 'fuzzy') return cell.value;
    if (cell.kind === 'linguistic') return BWM_LINGUISTIC_TO_TFN[cell.value] || [1, 1, 1];
    if (cell.kind === 'number') {
      const c = Number(cell.value);
      return [c, c, c];
    }
    return 0;
  }

  if (v === 'linguistic fuzzy') {
    if (isDiagonal) return 'EI';
    if (!cell || cell.kind === 'zero') return 0;
    if (cell.kind === 'linguistic') return cell.value;
    if (cell.kind === 'fuzzy') return nearestLinguisticByTfn(cell.value);
    if (cell.kind === 'number') return nearestLinguisticByCenter(cell.value);
    return 0;
  }

  // Fallback to crisp
  if (isDiagonal) return 1;
  return canonicalToCenterValue(cell);
}

function detectBestWorstIndicesFromCanonical(canonicalMatrix) {
  const n = canonicalMatrix.length;
  if (!n) return { bestIdx: -1, worstIdx: -1 };

  const isActive = (cell) => {
    if (!cell || cell.kind === 'invalid' || cell.kind === 'zero') return false;
    if (cell.kind === 'number') return cell.value !== 0;
    if (cell.kind === 'fuzzy') return !isZeroLikeCell(cell.value);
    if (cell.kind === 'linguistic') return true;
    return false;
  };

  let bestIdx = -1;
  for (let i = 0; i < n; i++) {
    let ok = true;
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      if (!isActive(canonicalMatrix[i][j])) {
        ok = false;
        break;
      }
    }
    if (ok) {
      bestIdx = i;
      break;
    }
  }

  let worstIdx = -1;
  for (let j = 0; j < n; j++) {
    let ok = true;
    for (let i = 0; i < n; i++) {
      if (i === j) continue;
      if (!isActive(canonicalMatrix[i][j])) {
        ok = false;
        break;
      }
    }
    if (ok) {
      worstIdx = j;
      break;
    }
  }

  return { bestIdx, worstIdx };
}

function readCsvRows(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      complete: ({ data }) => resolve(data || []),
      error: reject,
    });
  });
}

async function readXlsxRows(file) {
  const arrayBuffer = await file.arrayBuffer();
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('No sheet found.');
  const ws = wb.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
}

async function readRowsByFile(file) {
  const name = (file?.name || '').toLowerCase();
  const type = file?.type || '';

  if (name.endsWith('.csv') || type.includes('csv')) return readCsvRows(file);
  if (
    name.endsWith('.xlsx') ||
    type.includes('sheet') ||
    type.includes('excel')
  ) {
    return readXlsxRows(file);
  }
  throw new Error('Unsupported file type. Please upload .csv or .xlsx');
}

export async function importBWMMatrixFlexibleFile(file, targetVariant = 'linear') {
  const rows = await readRowsByFile(file);
  if (!rows.length) throw new Error('Empty file.');

  const header = (rows[0] || []).map((x) => String(x ?? '').trim());
  const criteria = header.slice(1).filter(Boolean);
  const n = criteria.length;
  if (n < 2) throw new Error('Header must list at least 2 criteria.');

  const body = rows.slice(1);
  if (body.length < n) throw new Error(`Matrix must have ${n} rows.`);

  const canonicalMatrix = Array.from({ length: n }, (_, i) => {
    const r = body[i] || [];
    return Array.from({ length: n }, (_, j) => parseBwmAnyCell(r[j + 1]));
  });

  const invalidCells = [];
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (canonicalMatrix[i][j]?.kind === 'invalid') {
        invalidCells.push(`(${i + 1},${j + 1})="${canonicalMatrix[i][j].value}"`);
      }
    }
  }
  if (invalidCells.length) {
    throw new Error(
      `Unsupported value(s) in matrix: ${invalidCells.slice(0, 5).join(', ')}${invalidCells.length > 5 ? ' ...' : ''}`
    );
  }

  const { bestIdx, worstIdx } = detectBestWorstIndicesFromCanonical(canonicalMatrix);

  const matrix = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) =>
      canonicalToTargetCell(canonicalMatrix[i][j], targetVariant, i, j)
    )
  );

  return { criteria, matrix, bestIdx, worstIdx };
}

